import { clamp } from "@common/helpers/collections";

export interface FillColumnInput {
    id: string;
    size: number;
    minSize: number;
    maxSize: number;
    /** Grow factor; `undefined` keeps the column at its size. */
    fill: number | undefined;
}

interface FillColumnsInput {
    columns: readonly FillColumnInput[];
    containerWidth: number;
}

export const isFillColumn = ({ fill }: { fill?: number }) =>
    fill !== undefined && fill > 0;

/** Whole-pixel bounds: a width never passes a fractional `maxSize` or `minSize`. */
const getMaxWidth = (column: FillColumnInput) => Math.floor(column.maxSize);
const getMinWidth = (column: FillColumnInput) => Math.ceil(column.minSize);

const sumFactors = (columns: readonly FillColumnInput[]) =>
    columns.reduce((sum, column) => sum + (column.fill ?? 0), 0);

/**
 * Moves `amount` px into fill columns (out of them when negative), split by
 * grow factor. Each stops at its `maxSize` (or `minSize`) and passes the rest
 * on to the others; the rounding remainder goes to the last open columns
 * that still have room.
 */
const distribute = ({
    widths,
    columns,
    amount,
}: {
    widths: Record<string, number>;
    columns: readonly FillColumnInput[];
    amount: number;
}) => {
    const direction = Math.sign(amount);
    const limitOf = (column: FillColumnInput) =>
        direction > 0 ? getMaxWidth(column) : getMinWidth(column);
    const roomOf = (column: FillColumnInput) =>
        (limitOf(column) - widths[column.id]) * direction;
    let remaining = Math.abs(amount);
    let openColumns = columns.filter(
        (column) => isFillColumn(column) && roomOf(column) > 0,
    );
    while (remaining > 0 && openColumns.length > 0) {
        const totalFactor = sumFactors(openColumns);
        const shareOf = (column: FillColumnInput) =>
            (remaining * (column.fill ?? 0)) / totalFactor;
        const reachedColumns = openColumns.filter(
            (column) => roomOf(column) <= shareOf(column),
        );
        if (reachedColumns.length === 0) {
            let distributed = 0;
            for (const column of openColumns) {
                const share = Math.floor(shareOf(column));
                widths[column.id] += share * direction;
                distributed += share;
            }
            let remainder = remaining - distributed;
            for (const column of openColumns.toReversed()) {
                const extra = Math.min(remainder, roomOf(column));
                widths[column.id] += extra * direction;
                remainder -= extra;
            }
            return;
        }
        for (const column of reachedColumns) {
            remaining -= roomOf(column);
            widths[column.id] = limitOf(column);
        }
        openColumns = openColumns.filter(
            (column) => !reachedColumns.includes(column),
        );
    }
};

/**
 * Rendered column widths in px. Every column starts at its clamped size; fill
 * columns then take the leftover container width, or give up width down to
 * their `minSize` when the columns overflow, split by grow factor. Widths are
 * whole pixels and add up to the container width whenever a fill column still
 * has room; past that the table scrolls.
 */
export const computeColumnWidths = ({
    columns,
    containerWidth,
}: FillColumnsInput): Record<string, number> => {
    const widths: Record<string, number> = {};
    for (const column of columns) {
        widths[column.id] = clamp(
            Math.round(column.size),
            getMinWidth(column),
            getMaxWidth(column),
        );
    }
    const baseTotal = columns.reduce(
        (sum, column) => sum + widths[column.id],
        0,
    );
    const leftover = Math.floor(containerWidth) - baseTotal;
    if (leftover !== 0 && containerWidth > 0) {
        distribute({ widths, columns, amount: leftover });
    }
    return widths;
};

/**
 * `columnLayout: "center"`: the inset before the first column and after the
 * last, half the width the columns leave in whole pixels; 0 once they fill or
 * overflow the container.
 */
export const getColumnsInset = ({
    containerWidth,
    totalWidth,
}: {
    containerWidth: number;
    totalWidth: number;
}): number => Math.max(0, Math.floor((containerWidth - totalWidth) / 2));
